import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { PageHeader } from "./fordon";
import { HubEditorial, faqSchema } from "@/components/HubEditorial";
import { husbilsdackFaq, husbilsdackSections } from "@/data/nationella-guider-3";
import {
  husbilsdackExtraFaq,
  husbilsdackExtraSections,
  husbilsdackTldr,
  GUIDE_AUTHOR,
  GUIDE_UPDATED,
} from "@/data/nationella-guider-3-extra";
import hero from "@/assets/guider/husbilsdack.jpg";

const URL = "https://www.akhusbilfritid.se/husbilsdack";

const sections = [...husbilsdackSections, ...husbilsdackExtraSections];
const faq = [...husbilsdackFaq, ...husbilsdackExtraFaq];

export const Route = createFileRoute("/husbilsdack")({
  // FAQ-schemat byggs i loadern så guidens textdata hamnar i sidans egen
  // chunk i stället för i initial-bundlen.
  codeSplitGroupings: [["loader", "component"]],
  loader: () => ({ faqLd: faqSchema(faq) }),
  head: ({ loaderData }) => ({
    meta: [
      { title: "Husbilsdäck 2026 – tryck per axel, 6-årsgränsen & vinterkrav" },
      {
        name: "description",
        content:
          "Typiskt 4,5 bar fram och 5,5 bar bak, byt vid 6 år oavsett mönster, vinterdäck krävs 1 dec–31 mars och en hel omgång CP-däck kostar 10 500–15 500 kr monterad. Verkstad i Kalix.",
      },
      {
        name: "keywords",
        content:
          "husbilsdäck, husbil däck, cp däck, husbil däcktryck, husbil vinterdäck, husbilsdäck pris",
      },
      { property: "og:type", content: "article" },
      { property: "og:title", content: "Husbilsdäck – CP-däck, lufttryck, ålder och vinterkrav" },
      {
        property: "og:description",
        content: "Tryck per axel, DOT-koden som avgör bytet och priser för en hel omgång 2026.",
      },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "@id": `${URL}#article`,
          headline: "Husbilsdäck – CP-däck, lufttryck, ålder och vinterkrav",
          url: URL,
          inLanguage: "sv-SE",
          author: { "@type": "Organization", name: "AK Husbil & Fritid" },
          publisher: { "@type": "Organization", name: "AK Husbil & Fritid" },
        }),
      },
      ...(loaderData ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.faqLd) }] : []),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Hem", item: "https://www.akhusbilfritid.se/" },
            { "@type": "ListItem", position: 2, name: "Guider", item: "https://www.akhusbilfritid.se/guider" },
            { "@type": "ListItem", position: 3, name: "Husbilsdäck", item: URL },
          ],
        }),
      },
    ],
  }),
  component: Page,
});

const facts = [
  { k: "6 år", v: "vår bytesgräns för däck i drift, oavsett mönsterdjup" },
  { k: "4,5 / 5,5 bar", v: "typiskt tryck fram/bak på fullastad halvintegrerad" },
  { k: "10,5–15,5 tkr", v: "komplett omgång CP-däck monterad 2026" },
  { k: "1 dec–31 mars", v: "vinterdäckskrav vid vinterväglag, även husbil" },
];

function Page() {
  return (
    <>
      <PageHeader
        label="Nationell guide · Däck"
        title={
          <>
            Husbilsdäck — <span className="font-italic-display text-accent">åldern</span> avgör, inte mönstret.
          </>
        }
        sub="Varför CP-däck är en egen kategori, hur du sätter trycket per axel efter vägning, vad DOT-koden betyder och vad en hel omgång kostar."
      />

      <section className="container-edge pt-10 md:pt-14">
        <figure className="overflow-hidden rounded-3xl border border-border">
          <img
            src={hero}
            alt="Närbild på husbilsdäck med djupt mönster på blöt vinterasfalt"
            width={1600}
            height={912}
            className="h-auto w-full object-cover"
            decoding="async"
          />
        </figure>
      </section>

      <section className="container-edge pt-10 md:pt-14">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {facts.map((f) => (
            <div key={f.k} className="rounded-3xl border border-border bg-card p-6">
              <p className="font-display text-3xl leading-none text-accent">{f.k}</p>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.v}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="container-edge pt-10 md:pt-14">
        <div className="rounded-3xl border border-border bg-card p-7 md:p-10">
          <p className="eyebrow">Kort svar</p>
          <ul className="mt-5 grid gap-3 text-sm leading-relaxed text-foreground/85 md:text-[15px]">
            <li>
              <strong>Tryck:</strong> sätt trycket efter vägd axellast, inte efter bilens dekal. På en fullastad
              halvintegrerad landar vi oftast på 4,5 bar fram och 5,5 bar bak — kontrollera kallt.
            </li>
            <li>
              <strong>Ålder:</strong> DOT-koden gäller före mönstret. Vi byter däck i drift vid 6 år, och senast vid 8
              år även om de ser nya ut.
            </li>
            <li>
              <strong>CP-däck:</strong> husbil ska ha CP-märkta däck (Camping/Pkw) som klarar hög last vid låg
              hastighet och långa stillastående perioder.
            </li>
            <li>
              <strong>Vinter:</strong> vinterdäckskravet vid vinterväglag 1 december–31 mars gäller även husbil över
              3,5 ton.
            </li>
            <li>
              <strong>Pris:</strong> en komplett omgång CP-däck monterad kostar 10 500–15 500 kr 2026 beroende på
              dimension och märke.
            </li>
          </ul>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/boka-service"
              className="inline-flex items-center gap-2 rounded-full bg-accent px-5 py-2.5 text-sm font-medium text-accent-foreground"
            >
              Boka däckbyte i Kalix <ArrowUpRight size={15} />
            </Link>
            <Link
              to="/besiktning-husbil"
              className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm font-medium"
            >
              Däckfel vid besiktning <ArrowUpRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      <div className="pt-14 md:pt-20">
        <HubEditorial
          eyebrow="Guiden"
          sections={sections}
          faq={faq}
          tldr={husbilsdackTldr}
          updated={GUIDE_UPDATED}
          author={GUIDE_AUTHOR}
        />
      </div>

      <section className="container-edge pb-20 md:pb-24">
        <div className="rounded-3xl border border-border bg-warm p-7 md:p-10">
          <p className="eyebrow">Läs vidare</p>
          <div className="mt-5 grid gap-3 sm:grid-cols-3">
            <Link
              to="/besiktning-husbil"
              className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition hover:border-accent"
            >
              <span className="text-sm font-medium">Besiktning — de sex vanligaste underkännandena</span>
              <ArrowUpRight size={16} className="shrink-0 text-accent transition group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/guider/$slug"
              params={{ slug: "husbil-vinterforvaring" }}
              className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition hover:border-accent"
            >
              <span className="text-sm font-medium">Vinterförvaring — checklista före undanställning</span>
              <ArrowUpRight size={16} className="shrink-0 text-accent transition group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/reservdelar"
              className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition hover:border-accent"
            >
              <span className="text-sm font-medium">Behöver du hjälp? Boka verkstad i Kalix</span>
              <ArrowUpRight size={16} className="shrink-0 text-accent transition group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
